import ko from 'knockout';

import { SUPPORTED_CURRENCIES } from '@/config/env';
import parseStringArray from '@/config/helpers/parseStringArray';
import { AccountingSoftware } from '@/features/accounting-software';
import { Instance as companyAccountingSoftwareService } from '@/features/accounting-software/services/companyAccountingSoftware';
import { getAuthorizationHeaderValue } from '@/features/api';
import type { Contact, TrackingCategoryOption } from '@/legacy/features/companies/documents/models';
import {
  AccountingSoftwareAccount,
  AccountingSoftwarePreferences,
  ApiResult,
  CompanyDocumentUploadSettings,
  ContactAddress,
  ContactDocumentUploadSettings,
  Currency,
  Document,
  DocumentBill,
  DocumentSubStatus,
  DocumentSuperStatus,
  DocumentType,
  EditorDocument,
  ExpenseItem,
  PaymentMethod,
  Supplier,
  TaxAccount,
  Terms,
  TrackingCategory,
} from '@/legacy/features/companies/documents/models';
import Customer from '@/legacy/features/companies/documents/models/customer';
import AuthFetchService from '@/legacy/services/authFetch';
import * as companyService from '@/legacy/services/company';
import * as userService from '@/legacy/services/user';

import BillsPage from '../models/billsPage';
import BillsRequest from '../models/billsRequest';

import DocumentError from './documentError';

function getCurrentUserId(): string {
  return userService.Instance.UserId();
}

function getCurrentCompanyId(): string {
  return companyService.Instance.CompanyId();
}

function apiURL(statics, ...dynamics): string {
  const finalUrl = [
    import.meta.env.FEATURE_DOCUMENTS_BASE_URL,
    import.meta.env.FEATURE_DOCUMENTS_API_BASE,
  ];
  statics.forEach((string, i) => {
    finalUrl.push(string, dynamics[i]);
  });
  return finalUrl.join('');
}

function getUpdateDocumentPayeeUrl(companyId: string): string {
  return apiURL`/company/${companyId}/document/payee`;
}

function documentListPage(companyId: string, page = 1): string {
  return apiURL`/files/${companyId}/${page}`;
}

function documentDataAPI(companyID: string, documentID: string): string {
  return apiURL`/company/${companyID}/document/${documentID}/data`;
}

function getCompanyDocumentsCountUrl(companyId: string): string {
  return apiURL`/companies/${companyId}/documents/count`;
}

function documentDataBulkAPI(companyID: string): string {
  return apiURL`/company/${companyID}/document`;
}

function documentDeleteDataBulkAPI(companyID: string): string {
  return apiURL`/company/${companyID}/document`;
}

function apiFileImageUrl(companyID: string, document: Document): string {
  return apiURL`/company/${companyID}/document/${document.id}/preview`;
}

function apiFileImageDownloadUrl(companyID: string): string {
  return apiURL`/company/${companyID}/document/download`;
}

function apiFileImagePreviewCodeUrl(companyID: string, documentId: string): string {
  return apiURL`/company/${companyID}/document/${documentId}/previewCode`;
}

function rawContactInfo(companyID: string, documentID: string): string {
  return apiURL`/company/${companyID}/document/${documentID}/rawcontactinfo`;
}

function signalrConnection(): string {
  return apiURL`/realtime/Negotiate`;
}

function signalrRegisterToCompany(): string {
  return apiURL`/realtime/RegisterToCompany`;
}

function documentAuditTrail(companyID: string, documentID: string): string {
  return apiURL`/company/${companyID}/document/${documentID}/audit`;
}

function getCompanyDocumentUploadSettingsUrl(
  companyId: string,
  accountingSoftware: string
): string {
  return apiURL`/companies/${companyId}/documentUploadSettings?accountingSoftware=${accountingSoftware}`;
}

function getContactDocumentUploadSettingsUrl(
  companyId: string,
  contactId: string,
  accountingSoftware: string
): string {
  return apiURL`/companies/${companyId}/contacts/${contactId}/documentUploadSettings?accountingSoftware=${accountingSoftware}`;
}

function getExpenseAccountsUrl(
  companyId: string,
  accountingSoftware: string,
  filterOutExcludedAccounts: boolean,
  includeAssetAndLiabilityAccounts: boolean,
  name?: string
): string {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/expenseAccounts`);
  const { searchParams } = url;
  searchParams.append('filterOutExcludedAccounts', filterOutExcludedAccounts.toString());
  searchParams.append(
    'includeAssetAndLiabilityAccounts',
    includeAssetAndLiabilityAccounts.toString()
  );
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getMarkAsPaidAccountsUrl(
  companyId: string,
  accountingSoftware: string,
  name?: string
): string {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/markAsPaidAccounts`);
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getAssetAndLiabilityAccountsUrl(
  companyId: string,
  accountingSoftware: string,
  name?: string
): string {
  const url = new URL(
    apiURL`/companies/${companyId}/${accountingSoftware}/assetAndLiabilityAccounts`
  );
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getPaymentAccountsUrl(
  companyId: string,
  accountingSoftware: string,
  paymentMethodId?: string,
  name?: string
): string {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/payment-accounts`);
  const { searchParams } = url;
  if (paymentMethodId) {
    searchParams.append('payment-method', paymentMethodId);
  }
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getTaxAccountsUrl(
  companyId: string,
  accountingSoftware: string,
  filterOutExcludedAccounts: boolean
): string {
  return apiURL`/companies/${companyId}/${accountingSoftware}/taxAccounts?filterOutExcludedAccounts=${filterOutExcludedAccounts}`;
}

function getExpenseItemsUrl(companyID: string, accountingSoftware: string, name?: string): string {
  const url = new URL(apiURL`/companies/${companyID}/${accountingSoftware}/items`);
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getCustomersUrl(companyID: string, accountingSoftware: string, name: string): string {
  return apiURL`/companies/${companyID}/${accountingSoftware}/customers?name=${name}`;
}

function documentPublishingUrl(companyID: string): string {
  return apiURL`/companies/${companyID}/bill`;
}

function getSuppliersUrl(companyId: string, accountingSoftware?: string, name?: string): string {
  const url = new URL(apiURL`/company/${companyId}/suppliers`);
  const { searchParams } = url;

  searchParams.append('accountingSoftware', accountingSoftware);

  if (name) {
    searchParams.append('name', name);
  }

  return url.toString();
}

function getCheckPermissionsUrl(companyID: string, accountingSoftware: string): string {
  return apiURL`/companies/${companyID}/${accountingSoftware}/permissions/bill/publish`;
}

function getCurrenciesUrl(companyId: string, accountingSoftware: string): string {
  return apiURL`/company/${companyId}/${accountingSoftware}/currencies`;
}

function getBillsUrl(companyId: string, query?: BillsQuery): string {
  const url = new URL(apiURL`/company/${companyId}/bills`);
  const { searchParams } = url;
  if (query) {
    Object.keys(query).forEach((key) => {
      if (key && query[key]) {
        searchParams.append(key, query[key]);
      }
    });
  }
  return url.toString();
}

function getSearchBillsUrl(companyId: string): string {
  const url = new URL(apiURL`/company/${companyId}/bills/search`);
  return url.toString();
}

function getBillsCurrenciesUrl(companyId: string): string {
  const url = new URL(apiURL`/company/${companyId}/bills/currencies`);
  return url.toString();
}

function getBillsByPaymentIdUrl(companyId: string, paymentId: string): string {
  return apiURL`/company/${companyId}/payments/${paymentId}/bills`;
}

function deleteBillsUrl(companyId: string): string {
  return apiURL`/company/${companyId}/bills`;
}

function createContactUrl(companyId: string, accountingSoftware: string): string {
  return apiURL`/companies/${companyId}/${accountingSoftware}/contacts`;
}

function getClassesUrl(companyId: string, accountingSoftware: string, name?: string) {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/classes`);
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getDepartmentsUrl(companyId: string, accountingSoftware: string, name?: string) {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/departments`);
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getLocationsUrl(companyId: string, accountingSoftware: string, name?: string) {
  const url = new URL(apiURL`/companies/${companyId}/${accountingSoftware}/locations`);
  const { searchParams } = url;
  if (name) {
    searchParams.append('name', name);
  }
  return url.toString();
}

function getPaymentMethodsUrl(companyId: string, accountingSoftware: string): string {
  return apiURL`/companies/${companyId}/${accountingSoftware}/payment-methods`;
}

function getTermsUrl(companyId: string, accountingSoftware: string): string {
  return apiURL`/companies/${companyId}/${accountingSoftware}/terms`;
}

function getRotateDocumentUrl(companyId: string, documentFileId: string): string {
  return apiURL`/company/${companyId}/document/${documentFileId}/rotate`;
}

function getTrackingCategoriesUrl(companyID: string, accountingSoftware: string): string {
  return apiURL`/companies/${companyID}/${accountingSoftware}/tracking-categories`;
}

function getAccountingSoftwarePreferencesUrl(companyId: string, accountingSoftware: string) {
  return apiURL`/companies/${companyId}/${accountingSoftware}/preferences`;
}

enum ErrorCodes {
  FailedToSendRequestToDocumentApi = 401,
  FailedToGetDownloadLinkDutToPermissions = 423,
}

const failedToSendRequestMessage = 'Failed to send request';

interface BillsQuery {
  orderBy?: string;
  orderDirection?: string;
  skip?: number;
  take?: number;
}

interface DocumentQuery {
  suppliers?: Array<Supplier>;
  /**
   * Possible Document statuses: Published (true | false).
   * Defaults to unpublished
   */
  superStatus?: DocumentSuperStatus;
  /**
   * Document status. Multiselect is possible.
   * Defaults to all
   */
  status?: Array<DocumentSubStatus>;
  /**
   * Document amount.
   */
  pageSize?: number;
  /**
   * The date type to sort documents with
   * Defaults to UploadDate
   */
  sortKey?: string;
  /**
   * The direction of sorting for value.
   * True for oldest to newest, where nulls are first (for other than UploadDate)
   */
  ascending?: boolean;
  startInvoiceDate?: string;
  endInvoiceDate?: string;
  documentType?: DocumentType | null;
}

interface DocumentsResponse extends ApiResult<Array<Document>> {
  total: number;
  hasNext: boolean;
  page: number;
}

type DocumentDataDictionary = { [id: string]: EditorDocument };

interface DocumentsUploadRequest {
  companyId: string;
  files: Array<File>;
}

interface DocumentsUploadResponse {
  isSuccess: boolean;
}

/**
 * Paginated response with next page indicator.
 */
interface HasNextResponse<T> {
  total: number;
  hasNext: boolean;
  data: T;
}

interface DocumentFileResponse {
  documentData: Blob;
  isPdf: boolean;
}

interface ContactData {
  readonly address?: string;
  readonly city?: string;
  readonly companyName?: string;
  readonly contactMail?: string;
  readonly contactName?: string;
  readonly country?: string;
  readonly customerNumber?: string;
  readonly postcode?: string;
  readonly taxNumber?: string;
  readonly vatId?: string;
}

interface AuditTrail {
  readonly date: string;
  readonly id: string;
  readonly email: string;
  readonly method: string;
  readonly name: string;
  readonly originalDocumentUri?: string;
  readonly publishedBy?: string;
  readonly publishedDate?: string;
}

interface ContactViewModel {
  id: string;
  name: string;
  email: string;
  displayName: string;
  phoneNumber: string;
  address: ContactAddress;
  accountNumber: string;
  defaultExpenseAccountId: string;
  defaultTaxAccountId: string;
  defaultCustomerId: string;
  defaultCurrency: string;
  defaultMemo: string;
  isCustomer: boolean;
  isSupplier: boolean;
  optionsText: string;
}

export default class DocumentService {
  static getCurrentAccountingSoftware() {
    return companyAccountingSoftwareService.softwareTypeObservable();
  }

  /** Check whether the current company's accounting software type is supported by the Documents feature. Not all types are. */
  static readonly isAccountingSoftwareSupported: KnockoutComputed<boolean> = ko.pureComputed(() => {
    const companySoftwareType = companyAccountingSoftwareService.softwareTypeObservable();
    const hasSupportedAccountingSoftware = parseStringArray(
      import.meta.env.FEATURE_DOCUMENTS_SUPPORTED_ACCOUNTING_SOFTWARE
    ).includes(companySoftwareType);
    return hasSupportedAccountingSoftware;
  });

  static async rotateDocument(documentId: string): Promise<void> {
    const url = getRotateDocumentUrl(getCurrentCompanyId(), documentId);
    return this.httpPost(url);
  }

  static async getCompanyDocumentsCount(): Promise<number> {
    const url = getCompanyDocumentsCountUrl(getCurrentCompanyId());
    return this.httpGet(url);
  }

  static async patchDocumentsPayee(
    documentId: string,
    plootoContactId: string,
    accountingSoftwareContactId: string,
    name: string
  ): Promise<void> {
    const url = new URL(getUpdateDocumentPayeeUrl(getCurrentCompanyId()));

    const body = {
      documentId,
      supplier: {
        payeeId: plootoContactId,
        contactId: accountingSoftwareContactId,
        name,
      },
    };

    return this.httpPatch(url, body);
  }

  static async getCurrencies(): Promise<Array<Currency>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported()) {
      return [];
    }

    if (!accountingSoftware) {
      return SUPPORTED_CURRENCIES.map((code: string) => ({
        id: code,
        name: code,
        code,
      }));
    }

    const url = getCurrenciesUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  static async getEditorDocuments(fileId: string): Promise<EditorDocument> {
    const url = documentDataAPI(getCurrentCompanyId(), fileId);
    return this.httpGet(url);
  }

  static async deleteBills(documentBillIds: Array<string>): Promise<any> {
    const url = new URL(deleteBillsUrl(getCurrentCompanyId()));

    return AuthFetchService.httpDelete(url.toString(), JSON.stringify({ documentBillIds }));
  }

  static async getBills(query?: BillsQuery): Promise<any> {
    const url = getBillsUrl(getCurrentCompanyId(), query);

    return this.httpGetRaw<any>(url);
  }

  static async getBillsCurrencies(): Promise<Array<string>> {
    const url = getBillsCurrenciesUrl(getCurrentCompanyId());

    return this.httpGet<Array<string>>(url);
  }

  static async searchBills(request?: BillsRequest): Promise<BillsPage> {
    const url = getSearchBillsUrl(getCurrentCompanyId());

    return this.httpPostRaw<BillsPage>(url, request);
  }

  static async getBillsByPaymentId(paymentId: string): Promise<Array<DocumentBill>> {
    const url = getBillsByPaymentIdUrl(getCurrentCompanyId(), paymentId);

    return this.httpGet(url);
  }

  static async getDocumentsList(
    page = 1,
    query?: DocumentQuery
  ): Promise<HasNextResponse<Array<Document>>> {
    const url = new URL(documentListPage(getCurrentCompanyId(), page));

    return AuthFetchService.httpPost(url.toString(), JSON.stringify(query))
      .then((res) => res.json())
      .then((documentResponse: DocumentsResponse) => {
        if (!documentResponse.isSuccess) {
          throw new DocumentError(documentResponse.message, documentResponse.errorCode);
        }

        const { hasNext, data, total } = documentResponse;

        return { hasNext, data, total };
      });
  }

  static async getSuppliers(name?: string): Promise<Array<Supplier>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();
    const url = getSuppliersUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  private static fileUploadRequest: XMLHttpRequest = null;

  static cancelFileUpload(): void {
    if (this.fileUploadRequest) {
      const request = this.fileUploadRequest;
      this.fileUploadRequest = null;
      request.abort();
    }
  }

  static async uploadFiles(
    request: DocumentsUploadRequest,
    progressHandler: (uploaded: number, total: number) => void
  ): Promise<DocumentsUploadResponse> {
    const url = new URL(`${import.meta.env.FEATURE_DOCUMENTS_BASE_URL}/api/v1/upload`);

    if (request) {
      const data = new FormData();
      data.append('companyId', getCurrentCompanyId());

      request.files.forEach((file, index) => {
        data.append(`file${index}`, file, file.name);
      });

      return new Promise((resolve, reject) => {
        try {
          this.fileUploadRequest = new XMLHttpRequest();

          this.fileUploadRequest.onload = (event) => {
            const { status } = this.fileUploadRequest;
            this.fileUploadRequest = null;

            resolve({ isSuccess: status === 200 });
          };

          this.fileUploadRequest.onerror = () => {
            if (this.fileUploadRequest) {
              this.fileUploadRequest = null;

              resolve({ isSuccess: false });
            }
          };

          this.fileUploadRequest.open('POST', url.toString(), true);
          this.appendAuthorizationHeader(this.fileUploadRequest);

          this.fileUploadRequest.upload.addEventListener(
            'progress',
            (event: ProgressEvent) => {
              progressHandler(event.loaded, event.total);
            },
            false
          );
          this.fileUploadRequest.send(data);
        } catch (error) {
          reject(error);
        }
      });
    }

    return Promise.resolve(null);
  }

  private static appendAuthorizationHeader(xhr: XMLHttpRequest) {
    if (xhr) {
      xhr.setRequestHeader('Authorization', getAuthorizationHeaderValue());
    }
  }

  static async getDocument(document: Document): Promise<DocumentFileResponse> {
    return AuthFetchService.httpGet(apiFileImageUrl(getCurrentCompanyId(), document))
      .then((response) => {
        if (!response.ok) {
          throw new DocumentError(`${response.status} ${response.statusText}`, response.status);
        }
        const responseTuple: [Promise<Blob>, boolean] = [
          response.blob(),
          response.headers.get('Content-Type')?.includes('application/pdf'),
        ];
        return responseTuple;
      })
      .then(async (responseTuple) => {
        const documentData = await responseTuple[0];
        const isPdf = responseTuple[1];
        return { documentData, isPdf };
      });
  }

  static createDocumentDownloadLinks(documentIds: Array<string>): Promise<Array<string>> {
    const url = apiFileImageDownloadUrl(getCurrentCompanyId());
    return this.httpPost(url, { documentIds });
  }

  static async createDocumentPreviewLink(documentId: string): Promise<string> {
    const url = apiFileImagePreviewCodeUrl(getCurrentCompanyId(), documentId);
    const code = await this.httpPost(url);

    const uri = apiFileImageUrl(getCurrentCompanyId(), { id: documentId } as Document);
    return `${uri}?code=${code}`;
  }

  /**
   * Update document data in whole or partially for a specific document.
   * @param id Document ID to modify.
   * @param document The data to update.
   * @returns ```void```
   * @throws If HTTP response code ```< 200``` or ```> 299```.
   */
  static async patchDocument(
    id: string,
    document: Partial<EditorDocument>
  ): Promise<EditorDocument> {
    const url = new URL(documentDataAPI(getCurrentCompanyId(), id));
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();
    if (accountingSoftware) {
      url.searchParams.append('accountingSoftware', accountingSoftware);
    }
    return this.httpPatch(url, document);
  }

  /**
   * Update document data in whole or partially for several documents.
   * @param ids Document IDs to modify.
   * @param document The data to update.
   * @returns ```void```
   * @throws If HTTP response code ```< 200``` or ```> 299```.
   */
  static async bulkPatchDocument(
    ids: Array<string>,
    document: Partial<EditorDocument>
  ): Promise<Array<EditorDocument>> {
    const url = new URL(documentDataBulkAPI(getCurrentCompanyId()));
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();
    if (accountingSoftware) {
      url.searchParams.append('accountingSoftware', accountingSoftware);
    }
    ids.forEach((id) => url.searchParams.append('id', id));
    return this.httpPatch(url, document);
  }

  static async getDocumentContact(documentID: string): Promise<ContactData> {
    const url = rawContactInfo(getCurrentCompanyId(), documentID);
    return this.httpGet(url);
  }

  static async getSignalrCredentials(): Promise<{ accessToken: string; url: string }> {
    const url = new URL(signalrConnection());
    return AuthFetchService.httpGet(url.toString(), {
      'X-Plooto-UserId': getCurrentUserId(),
      'X-Plooto-CompanyId': getCurrentCompanyId(),
    }).then((res) => {
      if (!res.ok) {
        throw new DocumentError(
          `can't get SignalR connection data; status "${res.status}"`,
          res.status
        );
      }
      return res.json();
    });
  }

  static async deleteDocuments(documentIds: Array<string>): Promise<void> {
    const url = new URL(documentDeleteDataBulkAPI(getCurrentCompanyId()));
    documentIds.forEach((id) => url.searchParams.append('id', id));
    return AuthFetchService.httpDelete(url.toString()).then((res) => {
      if (!res.ok) {
        throw new DocumentError(`can't delete document(s); status "${res.status}"`, res.status);
      }
    });
  }

  static async registerToCompanyEvents(): Promise<void> {
    const url = new URL(signalrRegisterToCompany());
    return AuthFetchService.httpPost(url.toString(), null, {
      'X-Plooto-CompanyId': getCurrentCompanyId(),
    }).then((res) => {
      if (!res.ok) {
        throw new DocumentError(
          `can't register to SignalR group; status "${res.status}"`,
          res.status
        );
      }
    });
  }

  static async getAuditTrail(documentId: string): Promise<AuditTrail> {
    const url = documentAuditTrail(getCurrentCompanyId(), documentId);
    return this.httpGet(url);
  }

  static saveCompanyDocumentUploadSettings(
    companyDocumentUploadSettings: CompanyDocumentUploadSettings
  ): Promise<void> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    const url = new URL(
      getCompanyDocumentUploadSettingsUrl(getCurrentCompanyId(), accountingSoftware)
    );

    return AuthFetchService.httpPut(
      url.toString(),
      JSON.stringify(companyDocumentUploadSettings)
    ).then((res) => {
      if (!res.ok) {
        throw res.json();
      }
    });
  }

  static async getCompanyDocumentUploadSettings(): Promise<CompanyDocumentUploadSettings> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    const url = getCompanyDocumentUploadSettingsUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  static async getContactDocumentUploadSettings(
    contactId: string
  ): Promise<ContactDocumentUploadSettings> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    const url = getContactDocumentUploadSettingsUrl(
      getCurrentCompanyId(),
      contactId,
      accountingSoftware
    );

    return this.httpGet(url);
  }

  static async updateContactDocumentAutoSyncSetting(
    autoSync: boolean,
    contactId: string
  ): Promise<void> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    const url = getContactDocumentUploadSettingsUrl(
      getCurrentCompanyId(),
      contactId,
      accountingSoftware
    );

    return AuthFetchService.httpPatch(
      url.toString(),
      JSON.stringify([
        {
          op: 'replace',
          path: '/autoSync',
          value: autoSync,
        },
      ])
    ).then((res) => {
      if (!res.ok) {
        throw res.json();
      }
    });
  }

  static async getExpenseAccounts(
    filterOutExcludedAccounts = true,
    includeAssetAndLiabilityAccounts = true,
    name: string = undefined
  ): Promise<Array<AccountingSoftwareAccount>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getExpenseAccountsUrl(
      getCurrentCompanyId(),
      accountingSoftware,
      filterOutExcludedAccounts,
      includeAssetAndLiabilityAccounts,
      name
    );

    return this.httpGet(url);
  }

  static async getMarkAsPaidAccounts(name?: string): Promise<Array<AccountingSoftwareAccount>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getMarkAsPaidAccountsUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getAssetAndLiabilityAccounts(
    name?: string
  ): Promise<Array<AccountingSoftwareAccount>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getAssetAndLiabilityAccountsUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getPaymentAccounts(
    paymentMethodId?: string,
    name?: string
  ): Promise<Array<AccountingSoftwareAccount>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getPaymentAccountsUrl(
      getCurrentCompanyId(),
      accountingSoftware,
      paymentMethodId,
      name
    );

    return this.httpGet(url);
  }

  static async getTaxAccounts(filterOutExcludedAccounts = true): Promise<Array<TaxAccount>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getTaxAccountsUrl(
      getCurrentCompanyId(),
      accountingSoftware,
      filterOutExcludedAccounts
    );

    return this.httpGet(url);
  }

  static async createContact(contact: Contact): Promise<Contact> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      throw new DocumentError('not connected to accounting software');
    }

    const url = createContactUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpPost(url, contact);
  }

  static async getCustomers(name?: string): Promise<Array<Customer>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getCustomersUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getExpenseItems(name?: string): Promise<Array<ExpenseItem>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return [];
    }

    const url = getExpenseItemsUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getClasses(name?: string): Promise<Array<TrackingCategoryOption>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported()) {
      return [];
    }

    if (
      accountingSoftware !== AccountingSoftware.QuickBooksOnline &&
      accountingSoftware !== AccountingSoftware.NetSuite
    ) {
      return [];
    }

    const url = getClassesUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getDepartments(name?: string): Promise<Array<TrackingCategoryOption>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported()) {
      return [];
    }

    if (
      accountingSoftware !== AccountingSoftware.QuickBooksOnline &&
      accountingSoftware !== AccountingSoftware.NetSuite
    ) {
      return [];
    }

    const url = getDepartmentsUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getLocations(name?: string): Promise<Array<TrackingCategoryOption>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported()) {
      return [];
    }

    if (accountingSoftware !== AccountingSoftware.NetSuite) {
      return [];
    }

    const url = getLocationsUrl(getCurrentCompanyId(), accountingSoftware, name);

    return this.httpGet(url);
  }

  static async getPaymentMethods(): Promise<Array<PaymentMethod>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (
      !this.isAccountingSoftwareSupported() ||
      accountingSoftware !== AccountingSoftware.QuickBooksOnline
    ) {
      return [];
    }

    const url = getPaymentMethodsUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  static async getTerms(): Promise<Array<Terms>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (
      !this.isAccountingSoftwareSupported() ||
      accountingSoftware !== AccountingSoftware.QuickBooksOnline
    ) {
      return [];
    }

    const url = getTermsUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  static async getAccountingSoftwarePreferences(): Promise<AccountingSoftwarePreferences> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (!this.isAccountingSoftwareSupported() || !accountingSoftware) {
      return undefined;
    }

    const url = getAccountingSoftwarePreferencesUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  static publishDocument(documentIds: Array<string>): Promise<boolean> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    const url = documentPublishingUrl(getCurrentCompanyId());

    return this.httpPost(url, { documentIds, accountingSoftware });
  }

  static async checkPermissions(): Promise<boolean> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (accountingSoftware !== AccountingSoftware.Xero) {
      // we need to check only Xero permissions for now
      return true;
    }

    const url = getCheckPermissionsUrl(getCurrentCompanyId(), accountingSoftware);

    try {
      return await this.httpGet(url);
    } catch {
      return false;
    }
  }

  static async getTrackingCategories(): Promise<Array<TrackingCategory>> {
    const accountingSoftware = DocumentService.getCurrentAccountingSoftware();

    if (accountingSoftware !== AccountingSoftware.Xero) {
      return [];
    }

    const url = getTrackingCategoriesUrl(getCurrentCompanyId(), accountingSoftware);

    return this.httpGet(url);
  }

  private static async httpGetRaw<T>(url: string): Promise<T> {
    let response: Response;

    try {
      response = await AuthFetchService.httpGet(url);
    } catch {
      throw new DocumentError(
        failedToSendRequestMessage,
        ErrorCodes.FailedToSendRequestToDocumentApi
      );
    }

    const json = await response.json();

    if (!json.isSuccess) {
      throw new DocumentError(json.message, json.errorCode);
    }

    return json as T;
  }

  /** @deprecated Use `ApiService` instead */
  private static async httpGet<T>(url: string): Promise<T> {
    const json: any = await this.httpGetRaw(url);

    return json.data as T;
  }

  /** @deprecated Use `ApiService` instead */
  private static async httpPost<T>(url: string, body?: any): Promise<T> {
    const json: any = await this.httpPostRaw(url, body);

    return json.data as T;
  }

  /** @deprecated Use `ApiService` instead */
  private static async httpPostRaw<T>(url: string, body?: any): Promise<T> {
    let response: Response;

    try {
      response = await AuthFetchService.httpPost(url.toString(), JSON.stringify(body), {
        'Content-Type': 'application/json',
      });
    } catch {
      throw new DocumentError(
        failedToSendRequestMessage,
        ErrorCodes.FailedToSendRequestToDocumentApi
      );
    }

    const json = await response.json();

    if (!json.isSuccess) {
      throw new DocumentError(json.message, json.errorCode);
    }

    return json as T;
  }

  /** @deprecated Use `ApiService` instead */
  private static async httpPatch<T>(url: URL, body?: any): Promise<T> {
    let response: Response;

    try {
      response = await AuthFetchService.httpPatch(url.toString(), JSON.stringify(body), {
        'Content-Type': 'application/json',
      });
    } catch {
      throw new DocumentError(
        failedToSendRequestMessage,
        ErrorCodes.FailedToSendRequestToDocumentApi
      );
    }

    const json = await response.json();

    if (!json.isSuccess) {
      throw new DocumentError(json.message, json.errorCode);
    }

    return json.data;
  }
}

export {
  BillsQuery,
  DocumentQuery,
  ContactData,
  AuditTrail,
  Contact,
  ContactViewModel,
  DocumentDataDictionary,
};
